import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Card,
  CardHeader,
  CardBody,
  OutlineButton,
  Badge,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableHeader,
  FlexContainer,
  Input,
  Select,
  Pagination,
  PageButton
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { SystemLog } from '../../types';
import apiService from '../../services/api';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const FilterSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  background-color: white;
  padding: 1rem;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  label {
    font-weight: 500;
    color: var(--dark-gray);
    white-space: nowrap;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 300px;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled(Card)`
  text-align: center;
  padding: 1rem;
  
  .stat-number {
    font-size: 2rem;
    font-weight: 600;
    color: var(--primary-color);
    margin-bottom: 0.5rem;
  }
  
  .stat-label {
    color: var(--dark-gray);
    font-size: 0.9rem;
  }
`;

const ActionBadge = styled(Badge)`
  min-width: 80px;
  text-align: center;
  font-size: 0.75rem;
`;

const SuccessBadge = styled(Badge)`
  min-width: 60px;
  text-align: center;
  font-size: 0.75rem;
`;

const UserInfo = styled.div`
  .user-name {
    font-weight: 600;
    color: var(--secondary-color);
  }
  
  .user-role {
    color: var(--dark-gray);
    font-size: 0.85rem;
  }
`;

const ResourceInfo = styled.div`
  .resource-type {
    font-weight: 500;
    color: var(--primary-color);
  }
  
  .resource-id {
    color: var(--dark-gray);
    font-size: 0.85rem;
  }
`;

const DateText = styled.span`
  color: var(--dark-gray);
  font-size: 0.85rem;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--dark-gray);
  
  .icon {
    font-size: 4rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
`;

const LogsList: React.FC = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<SystemLog[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [userFilter, setUserFilter] = useState<string>('');
  const [moduleFilter, setModuleFilter] = useState<string>('');
  const [actionFilter, setActionFilter] = useState<string>('');
  const [successFilter, setSuccessFilter] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllLogs, setShowAllLogs] = useState<boolean>(false);
  
  const itemsPerPage = 15;

  useEffect(() => {
    const loadSystemLogs = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Load logs based on toggle state
        const response = showAllLogs 
          ? await apiService.getSystemLogs({ page: 1, limit: 1000 })
          : await apiService.getMeaningfulLogs({ page: 1, limit: 1000 });
        
        if (response.success && response.data) {
          setLogs(response.data);
          setFilteredLogs(response.data);
        } else {
          setLogs([]);
          setFilteredLogs([]);
        }
      } catch (error: any) {
        console.error('Error loading system logs:', error);
        setError('Failed to load system logs');
        setLogs([]);
        setFilteredLogs([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (user) {
      loadSystemLogs();
    }
  }, [user, showAllLogs]);

  useEffect(() => {
    let result = [...logs];
    
    // Apply search filter
    if (searchTerm) {
      result = result.filter(
        log => 
          log.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
          log.module.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (log.resourceId && log.resourceId.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (log.resourceType && log.resourceType.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply user filter
    if (userFilter) {
      result = result.filter(log => log.userId === userFilter);
    }
    
    // Apply module filter
    if (moduleFilter) {
      result = result.filter(log => log.module === moduleFilter);
    }
    
    // Apply action filter
    if (actionFilter) {
      result = result.filter(log => log.action === actionFilter);
    }
    
    // Apply success filter
    if (successFilter) {
      result = result.filter(log => 
        successFilter === 'success' ? log.success : !log.success
      );
    }
    
    // Apply date filter
    if (dateFilter) {
      const filterDate = new Date(dateFilter);
      result = result.filter(log => {
        const logDate = new Date(log.timestamp);
        return logDate.toDateString() === filterDate.toDateString();
      });
    }
    
    // Sort by timestamp (newest first)
    result.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    
    setFilteredLogs(result);
    setCurrentPage(1);
  }, [logs, searchTerm, userFilter, moduleFilter, actionFilter, successFilter, dateFilter]);

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredLogs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const getActionVariant = (action: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' | 'secondary' => {
    switch (action.toLowerCase()) {
      case 'login':
      case 'create':
      case 'approve': return 'success';
      case 'delete':
      case 'reject': return 'danger';
      case 'update':
      case 'edit': return 'warning';
      case 'view':
      case 'download': return 'info';
      case 'submit': return 'primary';
      default: return 'secondary';
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  };

  // Calculate stats
  const totalLogs = logs.length;
  const successfulActions = logs.filter(log => log.success).length;
  const failedActions = logs.filter(log => !log.success).length;
  const todayLogs = logs.filter(log => {
    const today = new Date();
    const logDate = new Date(log.timestamp);
    return logDate.toDateString() === today.toDateString();
  }).length;

  // Get unique values for filters
  const uniqueUsers = Array.from(new Set(logs.map(log => ({ id: log.userId, name: log.userName }))));
  const uniqueModules = Array.from(new Set(logs.map(log => log.module)));
  const uniqueActions = Array.from(new Set(logs.map(log => log.action)));

  // Check if user has access to logs (only MD and TD roles)
  const canViewLogs = user?.role === 'MD' || user?.role === 'TD';

  if (!canViewLogs) {
    return (
      <Card>
        <CardBody>
          <NoDataMessage>
            <div className="icon">🔒</div>
            <h3>Access Denied</h3>
            <p>You don't have permission to view system logs.</p>
          </NoDataMessage>
        </CardBody>
      </Card>
    );
  }

  return (
    <div>
      <PageHeader>
        <h1 className="page-title">
          {showAllLogs ? 'All System Logs' : 'Meaningful Action Logs'}
        </h1>
        <FlexContainer gap="0.5rem">
          <OutlineButton 
            onClick={() => setShowAllLogs(!showAllLogs)}
            style={{ 
              backgroundColor: showAllLogs ? 'var(--warning-color)' : 'var(--success-color)',
              color: 'white'
            }}
          >
            {showAllLogs ? 'Show Meaningful Only' : 'Show All Logs'}
          </OutlineButton>
          <OutlineButton>
            Export Logs
          </OutlineButton>
          <OutlineButton>
            Archive Old Logs
          </OutlineButton>
        </FlexContainer>
      </PageHeader>

      <StatsContainer>
        <StatCard>
          <div className="stat-number">{totalLogs}</div>
          <div className="stat-label">Total Logs</div>
        </StatCard>
        <StatCard>
          <div className="stat-number">{successfulActions}</div>
          <div className="stat-label">Successful Actions</div>
        </StatCard>
        <StatCard>
          <div className="stat-number">{failedActions}</div>
          <div className="stat-label">Failed Actions</div>
        </StatCard>
        <StatCard>
          <div className="stat-number">{todayLogs}</div>
          <div className="stat-label">Today's Logs</div>
        </StatCard>
      </StatsContainer>

      <FilterSection>
        <SearchContainer>
          <Input 
            type="text" 
            placeholder="Search logs..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>

        <FilterGroup>
          <label>User:</label>
          <Select 
            value={userFilter} 
            onChange={(e) => setUserFilter(e.target.value)}
          >
            <option value="">All Users</option>
            {uniqueUsers.map(user => (
              <option key={user.id} value={user.id}>{user.name}</option>
            ))}
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>Module:</label>
          <Select 
            value={moduleFilter} 
            onChange={(e) => setModuleFilter(e.target.value)}
          >
            <option value="">All Modules</option>
            {uniqueModules.map(module => (
              <option key={module} value={module}>{module}</option>
            ))}
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>Action:</label>
          <Select 
            value={actionFilter} 
            onChange={(e) => setActionFilter(e.target.value)}
          >
            <option value="">All Actions</option>
            {uniqueActions.map(action => (
              <option key={action} value={action}>{action}</option>
            ))}
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>Status:</label>
          <Select 
            value={successFilter} 
            onChange={(e) => setSuccessFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="success">Success</option>
            <option value="failure">Failure</option>
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>Date:</label>
          <Input 
            type="date" 
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />
        </FilterGroup>

        <OutlineButton onClick={() => {
          setSearchTerm('');
          setUserFilter('');
          setModuleFilter('');
          setActionFilter('');
          setSuccessFilter('');
          setDateFilter('');
        }}>
          Reset Filters
        </OutlineButton>
      </FilterSection>

      <Card>
        <CardHeader>
          <h3>System Logs ({filteredLogs.length})</h3>
        </CardHeader>
        <CardBody>
          {currentItems.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Timestamp</TableHeader>
                    <TableHeader>User</TableHeader>
                    <TableHeader>Action</TableHeader>
                    <TableHeader>Module</TableHeader>
                    <TableHeader>Resource</TableHeader>
                    <TableHeader>Status</TableHeader>
                  </TableRow>
                </TableHead>
                <tbody>
                  {currentItems.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <DateText>{formatDate(log.timestamp)}</DateText>
                      </TableCell>
                      <TableCell>
                        <UserInfo>
                          <div className="user-name">{log.userName}</div>
                          <div className="user-role">{log.userRole}</div>
                        </UserInfo>
                      </TableCell>
                      <TableCell>
                        <ActionBadge variant={getActionVariant(log.action)}>
                          {log.action}
                        </ActionBadge>
                      </TableCell>
                      <TableCell>{log.module}</TableCell>
                      <TableCell>
                        {log.resourceType && log.resourceId ? (
                          <ResourceInfo>
                            <div className="resource-type">{log.resourceType}</div>
                            <div className="resource-id">{log.resourceId}</div>
                          </ResourceInfo>
                        ) : (
                          <span style={{ color: 'var(--medium-gray)' }}>-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <SuccessBadge variant={log.success ? 'success' : 'danger'}>
                          {log.success ? 'Success' : 'Failed'}
                        </SuccessBadge>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          ) : (
            <NoDataMessage>
              <div className="icon">📋</div>
              <h3>No logs found</h3>
              <p>No system logs match your current filters.</p>
            </NoDataMessage>
          )}

          {totalPages > 1 && (
            <Pagination>
              <PageButton 
                onClick={() => paginate(currentPage - 1)} 
                disabled={currentPage === 1}
              >
                &laquo;
              </PageButton>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                <PageButton
                  key={number}
                  active={number === currentPage}
                  onClick={() => paginate(number)}
                >
                  {number}
                </PageButton>
              ))}
              
              <PageButton 
                onClick={() => paginate(currentPage + 1)} 
                disabled={currentPage === totalPages}
              >
                &raquo;
              </PageButton>
            </Pagination>
          )}
        </CardBody>
      </Card>

      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
    </div>
  );
};

export default LogsList; 